// Quick-note composer at the top of the ציר זמן tab (mock s17 `.ct-composer`).
// Writes to the user-notes collection with the same doc semantics as the notes
// feature (ported logic — see timelineData.addTimelineQuickNote) and reflects
// the note optimistically in the shared 'notes:{id}' + 'contact-details:{id}'
// session caches so both this tab and the notes/overview tabs update instantly.
import { useState } from "react";
import { Stack, Button, TextField } from "@material-ui/core";
import { useSnackbar } from "notistack5";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { addTimelineQuickNote, applyQuickNoteToCaches } from "./timelineData";

type TimelineComposerProps = {
  contactId: string;
};

export function TimelineComposer({ contactId }: TimelineComposerProps): JSX.Element {
  const { user } = useAuth();
  const { userSettings } = useFirestore();
  const { enqueueSnackbar } = useSnackbar();

  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const userEmail: string | undefined = user?.email;
  const canSave = !!text.trim() && !!userEmail && !saving;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !userEmail) return;
    setSaving(true);
    try {
      const note = await addTimelineQuickNote({ contactId, userEmail, text });
      applyQuickNoteToCaches(contactId, note, {
        email: userEmail,
        name: userSettings?.ownerFullName,
        avatar: userSettings?.photoURL
      });
      setText("");
    } catch {
      enqueueSnackbar("שמירת ההערה נכשלה, נסו שוב", {
        variant: "error",
        autoHideDuration: 4000
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack direction="row" gap={1} alignItems="flex-start" sx={{ mb: 2 }}>
      <TextField
        fullWidth
        multiline
        maxRows={4}
        size="small"
        value={text}
        dir="auto"
        placeholder="הוסיפו הערה לציר הזמן…"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Ctrl/Cmd+Enter submits (plain Enter keeps adding lines)
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void handleSave();
          }
        }}
      />
      <Button
        variant="contained"
        disabled={!canSave}
        onClick={() => void handleSave()}
        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
      >
        הוספת הערה
      </Button>
    </Stack>
  );
}
